import React from 'react'
import { currentLanguageCodeVar, currentMarketIdVar } from 'graphql/reactive'
import { searchResultClicked } from 'lib/tracking/avo/sdk'
import { Hit, HitsProvided } from 'react-instantsearch-core'
import { connectHitInsights, connectHits } from 'react-instantsearch-dom'
import aa from 'search-insights'

import { AlgoliaProfileHitProps } from 'components/SearchResults/AlgoliaProfileHitProps'
import ResponsiveProfileCard from 'components/SearchResults/ResponsiveProfileCard'
import SearchResultCard from 'components/SearchResults/SearchResultCard'
import useAlgoliaIndices from 'hooks/useAlgoliaIndices'
import useTrackAlgoliaSearch from 'hooks/useTrackAlgoliaSearch'

import { algoliaEventNames } from '@/constants'

import styles from './SearchResultList.module.css'

interface SearchResultListProps extends HitsProvided<AlgoliaProfileHitProps> {
  hits: Hit<AlgoliaProfileHitProps>[]
  potentialProfiles: boolean
}

type SearchHitProps = {
  hit: AlgoliaProfileHitProps
  potentialProfile?: boolean
}

type SearchHitProvidedProps = {
  insights: (event: string, data: Record<string, string>) => void
}

const SearchHit = (
  props: SearchHitProps &
    SearchHitProvidedProps & { extraLinkProps?: Record<string, unknown> },
) => {
  const { hit, insights, potentialProfile, extraLinkProps } = props
  const { storeSearchHitClick } = useTrackAlgoliaSearch()
  const { profilesIndex } = useAlgoliaIndices()
  const marketId = currentMarketIdVar()
  const languageId = currentLanguageCodeVar()

  return (
    <SearchResultCard
      key={hit.slug}
      slug={hit.slug}
      onClick={() => {
        storeSearchHitClick(
          // eslint-disable-next-line no-underscore-dangle
          hit.__queryID,
          hit.objectID,
          hit.profileId,
          profilesIndex,
        )
        insights('clickedObjectIDsAfterSearch', {
          eventName: algoliaEventNames.PRODUCT_CLICKED,
        })
        searchResultClicked({
          discountAmountUsd: null,
          discountPercentage: null,
          hasDiscount: null,
          localeId: languageId,
          marketId,
          productId: hit.productId,
          productName: null,
          productProductType: null,
          productSaleMode: null,
          productSlug: null,
          productVatType: null,
          profileAcceptingOrders: hit.profileAcceptingOrders,
          profileId: hit.profileId,
          profileName: hit.profileName,
          profileOnboardingCompletedAt: null,
          profileProductAcceptingOrders: hit.profileProductAcceptingOrders,
          profileProductId: hit.profileProductId,
          profileProductPriceCurrency: null,
          profileProductPriceIncVatUsd: null,
          profileSlug: hit.slug,
          profileTags: null,
          searchRank: hit.__position,
        })
      }}
      data-insights-object-id={hit.objectID}
      data-insights-position={hit.__position}
      data-insights-query-id={hit.__queryID}
      extraLinkProps={extraLinkProps}
    >
      <ResponsiveProfileCard hit={hit} potentialProfile={potentialProfile} />
    </SearchResultCard>
  )
}

const ConnectedHitInsightComponent =
  connectHitInsights<SearchHitProps>(aa)(SearchHit)

const SearchResultList = (props: SearchResultListProps): JSX.Element => {
  const { hits, potentialProfiles } = props
  const { profilesIndex } = useAlgoliaIndices()
  if (!hits || !hits.length) {
    return null
  }

  return (
    <section
      className={styles.profileCardGrid}
      data-insights-index={profilesIndex}
    >
      {hits.map((hit) => (
        <div key={hit.slug} className="profile-card-grid__item">
          <ConnectedHitInsightComponent
            hit={hit}
            potentialProfile={potentialProfiles}
          />
        </div>
      ))}
    </section>
  )
}

export const ConnectedSearchResultList = connectHits(SearchResultList)

export default SearchResultList
